import { API_BASE_URL } from '../config/api';
import React, { useEffect, useState } from 'react';

interface Release {
  _id: string;
  model: string;
  version: string;
  type: 'prod' | 'depr' | 'factory';
  addToChangelog?: boolean;
  releaseNotes: string;
  technicalDetails?: string;
  createdAt: string;
  createdBy?: {
    firstName: string;
    lastName: string;
    email: string;
  };
}

interface ApiResponse {
  releases: Release[];
}

const ReleasesHistory: React.FC = () => {
  const [releases, setReleases] = useState<Release[]>([]);
  const [models, setModels] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
        const url = `${API_BASE_URL}/releases/public/all`;
    
    fetch(url)
      .then(res => {
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        return res.json();
      })
      .then((data: ApiResponse) => {
        const releasesData = data.releases || [];
        setReleases(releasesData);
        
        // Extract unique models and sort them
        const uniqueModels = [...new Set(releasesData.map(r => r.model))].sort();
        setModels(uniqueModels);
        
        // Set the first model as active tab
        if (uniqueModels.length > 0) {
          setActiveTab(uniqueModels[0]);
        }
        
        setLoading(false);
      })
      .catch(err => {
        setError(`Failed to load releases: ${err.message}`);
        setLoading(false);
      });
  }, []);

  const compareVersions = (versionA: string, versionB: string) => {
    const aParts = versionA.replace(/^v/i, '').split('.').map(Number);
    const bParts = versionB.replace(/^v/i, '').split('.').map(Number);

    for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
      const aPart = Number.isFinite(aParts[i]) ? aParts[i] : 0;
      const bPart = Number.isFinite(bParts[i]) ? bParts[i] : 0;

      if (aPart !== bPart) {
        return aPart - bPart;
      }
    }

    return 0;
  };

  const getHighestProductionOrBetaVersion = (model: string) => {
    return releases
      .filter(release => release.model === model && ['prod', 'beta'].includes(release.type))
      .map(release => release.version)
      .sort((a, b) => compareVersions(b, a))[0];
  };

  const getReleasesForModel = (model: string) => {
    const highestProductionOrBetaVersion = getHighestProductionOrBetaVersion(model);

    return releases
      .filter(release => {
        if (release.model !== model) return false;
        if (release.type !== 'depr' || !highestProductionOrBetaVersion) return true;
        return compareVersions(release.version, highestProductionOrBetaVersion) <= 0;
      })
      .sort((a, b) => compareVersions(b.version, a.version));
  };

  const getTypeDisplayInfo = (type: string, model: string) => {
    // Check if there's a production release for this model
    const hasProductionRelease = releases.some(release => 
      release.model === model && release.type === 'prod'
    );

    switch (type) {
      case 'prod':
        return { badge: 'Current', color: 'success', borderClass: 'border-success', typeBadgeClass: 'text-bg-success' };
      case 'factory':
        // If no production release exists, factory becomes "Current"
        if (!hasProductionRelease) {
          return { badge: 'Current', color: 'success', borderClass: 'border-warning', typeBadgeClass: 'text-bg-warning text-dark' };
        }
        return { badge: '', color: 'warning', borderClass: 'border-warning', typeBadgeClass: 'text-bg-warning text-dark' };
      case 'depr':
        return { badge: '', color: 'secondary', borderClass: 'border-secondary', typeBadgeClass: 'text-bg-secondary' };
      default:
        return { badge: '', color: 'primary', borderClass: 'border-primary', typeBadgeClass: 'text-bg-primary' };
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'prod':
        return 'Production';
      case 'depr':
        return 'Deprecated';
      case 'factory':
        return 'Factory';
      default:
        return type;
    }
  };

  return (
    <div className="container-fluid py-3">
      <div className="responsive-page-header mb-4">
        <h3 className="mb-1">Firmware Releases</h3>
        <p className="text-muted mb-0">Review public firmware release notes and optional technical details.</p>
      </div>

      {loading && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status" />
          <p className="mt-2">Loading releases...</p>
        </div>
      )}

      {error && <div className="alert alert-danger">{error}</div>}

      {!loading && !error && models.length > 0 && (
        <div className="card shadow-sm border-0">
          <div className="card-header bg-white">
            <ul className="nav nav-tabs card-header-tabs flex-nowrap overflow-auto">
              {models.map(model => (
                <li className="nav-item" key={model}>
                  <button
                    type="button"
                    className={`nav-link text-nowrap ${activeTab === model ? 'active' : ''}`}
                    onClick={() => setActiveTab(model)}
                  >
                    {model}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="card-body">
            {getReleasesForModel(activeTab).length === 0 ? (
              <div className="alert alert-warning mb-0">No releases found for {activeTab}.</div>
            ) : (
              getReleasesForModel(activeTab).map(release => {
                const typeInfo = getTypeDisplayInfo(release.type, activeTab);
                const releaseNotes = release.releaseNotes?.trim();
                const technicalDetails = release.technicalDetails?.trim();

                return (
                  <div
                    key={release._id}
                    className={`card shadow-sm border-0 border-start border-4 ${typeInfo.borderClass} mb-3`}
                  >
                    <div className="card-body">
                      <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
                        <div>
                          <div className="d-flex flex-wrap align-items-center gap-2">
                            {typeInfo.badge && (
                              <span className={`badge bg-${typeInfo.color}`}>
                                {typeInfo.badge}
                              </span>
                            )}
                            <h5 className="mb-0">v{release.version}</h5>
                          </div>
                          <div className="text-muted small mt-1">
                            Released {new Date(release.createdAt).toLocaleDateString()}
                          </div>
                        </div>
                        <span className={`badge ${typeInfo.typeBadgeClass}`}>{getTypeLabel(release.type)}</span>
                      </div>

                      <div className="mt-3">
                        <h6 className="fw-bold">Release Notes</h6>
                        <div className="p-3 bg-light border rounded" style={{ whiteSpace: 'pre-wrap' }}>
                          {releaseNotes || <span className="text-muted">No release notes provided.</span>}
                        </div>
                      </div>

                      <details className="mt-3">
                        <summary className="fw-semibold text-muted" style={{ cursor: 'pointer' }}>
                          Technical Details
                        </summary>
                        <div className="p-3 bg-light border rounded mt-2" style={{ whiteSpace: 'pre-wrap' }}>
                          {technicalDetails || <span className="text-muted">No technical details provided.</span>}
                        </div>
                      </details>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {!loading && !error && models.length === 0 && (
        <div className="alert alert-warning">No releases found.</div>
      )}
    </div>
  );
};

export default ReleasesHistory; 